import { createElement, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type {BuildInfo} from '@/lib/build/buildInfo';
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import {  buildInfo, fetchApiBuildInfo } from '@/lib/build/buildInfo'

type Row = { label: string; value: string }

function buildInfoRows(info: BuildInfo | null, t: (k: string) => string): Array<Row> {
  if (!info) {
    return [{ label: t('aboutPage.fields.status'), value: t('aboutPage.unreachable') }]
  }
  return [
    { label: t('aboutPage.fields.version'), value: info.version },
    { label: t('aboutPage.fields.environment'), value: info.environment },
  ]
}

function BuildInfoCard({
  title,
  info,
  t,
}: {
  title: string
  info: BuildInfo | null
  t: (k: string) => string
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="p-4 border-b">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <dl>
        {buildInfoRows(info, t).map((row) => (
          <div
            key={row.label}
            className="flex items-start p-4 border-b last:border-b-0"
          >
            <dt className="w-[40%] shrink-0 text-sm text-muted-foreground">
              {row.label}
            </dt>
            <dd className="w-[60%] text-sm">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function AboutSettingsPage() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const [apiInfo, setApiInfo] = useState<BuildInfo | null>(null)
  const [apiLoading, setApiLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchApiBuildInfo().then((info) => {
      if (cancelled) return
      setApiInfo(info)
      setApiLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Diagnostics payload is intentionally a subset of build info — no commit
  // SHA, no codename, no build timestamp. Clipboard is opt-in so the user can
  // paste version/environment into a support thread without leaking anything
  // that isn't already available on the (sanitised) anonymous /version endpoint.
  const handleCopy = async () => {
    const diagnostics = {
      client: { version: buildInfo.version, environment: buildInfo.environment },
      api: apiInfo ? { version: apiInfo.version, environment: apiInfo.environment } : null,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2))
      setCopied(true)
      toast.success(t('aboutPage.toast.copied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(tCommon('errors.generic'))
    }
  }

  return (
    <SettingsPageContainer>
      <SettingsPageHeader title={t('aboutPage.title')} />

      <div className="space-y-6">
        <BuildInfoCard title={t('aboutPage.clientTitle')} info={buildInfo} t={t} />
        <BuildInfoCard
          title={t('aboutPage.apiTitle')}
          info={apiLoading ? null : apiInfo}
          t={t}
        />

        <div className="flex justify-end">
          <Button onClick={handleCopy} variant="outline" size="sm">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? t('aboutPage.copied') : t('aboutPage.copyDiagnostics')}
          </Button>
        </div>
      </div>
    </SettingsPageContainer>
  )
}

export default AboutSettingsPage

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/_internal/build',
)({
  component: AboutSettingsPage,
})
